import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, TrendingUp, Receipt, Layers, Zap, FileSpreadsheet, Upload, Download, Cloud } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SalesCockpit } from "@/components/SalesCockpit";
import { EditableMISBoard } from "@/components/EditableMISBoard";
import { EditableSheetGrid } from "@/components/EditableSheetGrid";
import { MISActivityFeed } from "@/components/MISActivityFeed";
import { MISBackups } from "@/components/MISBackups";
import { PlanAllocation } from "@/components/PlanAllocation";
import { SharePointSyncSettings } from "@/components/SharePointSyncSettings";
import { MISAnalytics } from "@/components/mis/MISAnalytics";

const cr = (v) => (Number(v || 0) / 1e7).toFixed(2);

function StatCard({ icon: Icon, label, value, suffix, tone = "slate", testid }) {
  const tones = { slate: "text-slate-700 bg-slate-100", brand: "text-brand bg-[#FFEAE7]", emerald: "text-emerald-700 bg-emerald-100", orange: "text-orange-700 bg-orange-100", blue: "text-blue-700 bg-blue-100", violet: "text-violet-700 bg-violet-100" };
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <div>
        <p className="font-heading text-xl font-bold text-slate-900 leading-none">{value}<span className="text-sm font-medium text-slate-400 ml-0.5">{suffix}</span></p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </Card>
  );
}

function LiveView() {
  const { data, isLoading } = useQuery({ queryKey: ["sd-live"], queryFn: async () => (await api.get("/sales-mis/live-dashboard")).data, refetchInterval: 60000 });
  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const t = data?.totals || {};
  const zones = data?.zones || [];
  return (
    <div>
      <p className="text-xs text-slate-400 mb-4 flex items-center gap-1.5"><Zap className="h-3 w-3 text-brand" /> Live from CRM · computed from deals & leads · grouped by dealer zone · INR Crore · All figures are in INR Cr</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatCard icon={TrendingUp} label="Booking (YTD)" value={cr(t.booking)} tone="brand" testid="live-stat-booking" />
        <StatCard icon={Receipt} label="Billing (Won)" value={cr(t.billing)} tone="emerald" testid="live-stat-billing" />
        <StatCard icon={Layers} label="Open Pipeline" value={cr(t.pipeline)} tone="violet" testid="live-stat-pipeline" />
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Zone</th>
                {["Booking", "Billing", "Pipeline"].map((h) => <th key={h} className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{h} (Cr)</th>)}
                {["Opps", "Leads"].map((h) => <th key={h} className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.zone} className="border-b border-slate-100 text-slate-600" data-testid={`live-row-${z.zone}`}>
                  <td className="text-left px-4 py-2 font-medium text-slate-800">{z.zone}</td>
                  <td className="text-right px-3 py-2 font-mono">{cr(z.booking)}</td>
                  <td className="text-right px-3 py-2 font-mono text-emerald-600">{cr(z.billing)}</td>
                  <td className="text-right px-3 py-2 font-mono text-violet-600">{cr(z.pipeline)}</td>
                  <td className="text-right px-3 py-2 font-mono">{z.opp_count}</td>
                  <td className="text-right px-3 py-2 font-mono">{z.leads_count}</td>
                </tr>
              ))}
              {zones.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400">No CRM deals to summarise yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function EditableSheetsTab() {
  const { data } = useQuery({ queryKey: ["edit-sheets"], queryFn: async () => (await api.get("/sales-mis/edit-sheets")).data });
  const sheets = data || [];
  const [slug, setSlug] = useState("");
  const active = slug || sheets[0]?.slug || "";
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <FileSpreadsheet className="h-4 w-4 text-slate-400" />
        <Select value={active} onValueChange={setSlug}>
          <SelectTrigger className="h-9 w-[300px]" data-testid="edit-sheet-selector"><SelectValue placeholder="Select a sheet" /></SelectTrigger>
          <SelectContent>{sheets.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.title}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {active ? <EditableSheetGrid key={active} slug={active} /> : <p className="text-slate-400 py-10 text-center text-sm">No sheets available.</p>}
    </div>
  );
}

export default function SalesMIS() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showSharePoint, setShowSharePoint] = useState(false);

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/sales-mis/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sales_MIS_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/region/upload-mis", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Sales MIS updated — ${(data.regions || []).join(", ")} refreshed (as of ${data.as_of}).`);
      ["region-dashboard", "sd", "sd-live", "mis-sheets"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const MIS_VIEW_ROLES = ["super_admin", "management", "sales_head", "region_head", "sales_manager"];
  const canViewMis = MIS_VIEW_ROLES.includes(user?.role);
  const canMonitor = ["super_admin", "sales_head", "management", "region_head"].includes(user?.role);

  return (
    <div>
      <PageHeader title="Sales MIS">
        <Button variant="outline" onClick={onExport} disabled={exporting} className="gap-1.5" data-testid="mis-export-btn">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {exporting ? "Preparing…" : "Download Excel"}
        </Button>
        {["super_admin", "sales_head"].includes(user?.role) && (
          <Button variant="outline" onClick={() => setShowSharePoint(true)} className="gap-1.5" data-testid="sharepoint-settings-btn">
            <Cloud className="h-4 w-4 text-sky-600" /> SharePoint Sync
          </Button>
        )}
        {user?.role === "super_admin" && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onUpload} className="hidden" data-testid="mis-upload-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-brand hover:bg-brand/90 text-white gap-1.5" data-testid="mis-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? "Updating…" : "Update Sales MIS"}
            </Button>
          </>
        )}
      </PageHeader>
      <Tabs defaultValue="analytics">
        <TabsList className="mb-5">
          <TabsTrigger value="analytics" data-testid="salesmis-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="board" data-testid="salesmis-tab-board">Sales Dash Board</TabsTrigger>
          <TabsTrigger value="plan" data-testid="salesmis-tab-plan">Plan Allocation</TabsTrigger>
          <TabsTrigger value="sheets" data-testid="salesmis-tab-sheets">Sheets</TabsTrigger>
          {canMonitor && <TabsTrigger value="activity" data-testid="salesmis-tab-activity">Activity</TabsTrigger>}
          {canMonitor && <TabsTrigger value="backups" data-testid="salesmis-tab-backups">Backups</TabsTrigger>}
          {canViewMis && <>
            <TabsTrigger value="cockpit" data-testid="salesmis-tab-cockpit">Cockpit (Live)</TabsTrigger>
            <TabsTrigger value="live" data-testid="salesmis-tab-live">Live by Zone</TabsTrigger>
          </>}
        </TabsList>
        <TabsContent value="analytics"><MISAnalytics /></TabsContent>
        <TabsContent value="board"><EditableMISBoard /></TabsContent>
        <TabsContent value="plan"><PlanAllocation /></TabsContent>
        <TabsContent value="sheets"><EditableSheetsTab /></TabsContent>
        {canMonitor && <TabsContent value="activity"><MISActivityFeed /></TabsContent>}
        {canMonitor && <TabsContent value="backups"><MISBackups canRun={["super_admin", "sales_head", "region_head"].includes(user?.role)} /></TabsContent>}
        {canViewMis && <>
          <TabsContent value="cockpit"><SalesCockpit /></TabsContent>
          <TabsContent value="live"><LiveView /></TabsContent>
        </>}
      </Tabs>
      {["super_admin", "sales_head"].includes(user?.role) && <SharePointSyncSettings open={showSharePoint} onOpenChange={setShowSharePoint} />}
    </div>
  );
}
